import { QueryKey } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { List, SortPayload } from 'react-admin';

import { LiveLogRange } from 'types/LiveLogs';
import { getListQueryPage, LIVE_LOGS_AUTO_REFRESH_MS, LIVE_LOGS_PER_PAGE } from './liveLogs';
import { LiveLogsError } from './LiveLogsError';
import { LiveLogsPagination } from './LiveLogsPagination';

/** Newest first, always; the API ignores sort, and no column is sortable. */
const SORT: SortPayload = { field: 'timestamp', order: 'DESC' };

/** The inline error state is the feedback. The default toast would fire on every auto-refresh. */
const silenceErrorToast = () => undefined;

/** Polls page 1 only, so rows do not shift under someone reading a later page. */
const pollFirstPage = (query: { queryKey: QueryKey }) =>
  getListQueryPage(query.queryKey) === 1 ? LIVE_LOGS_AUTO_REFRESH_MS : false;

interface LiveLogListProps {
  resource: string;
  range: LiveLogRange;
  autoRefresh: boolean;
  children: ReactNode;
}

/**
 * One source's list. The key remounts it on a range (or source) change, which starts it again at
 * page 1. Its state stays local: Show tab links carry the query string between tabs, and
 * ActivityLogTab's list writes `?page`/`sort` into it.
 *
 * `range` travels as request meta, which getQueryFromParams sends as a top-level query param.
 * react-query stops the interval on unmount and while the browser tab is hidden.
 */
export const LiveLogList = ({ resource, range, autoRefresh, children }: LiveLogListProps) => (
  <List
    key={`${resource}:${range}`}
    resource={resource}
    disableSyncWithLocation
    title={false}
    actions={false}
    empty={false}
    error={<LiveLogsError />}
    sort={SORT}
    perPage={LIVE_LOGS_PER_PAGE}
    pagination={<LiveLogsPagination />}
    queryOptions={{
      meta: { range },
      refetchInterval: autoRefresh ? pollFirstPage : false,
      onError: silenceErrorToast,
    }}
  >
    {children}
  </List>
);
